import { useState, useEffect } from 'react';
import { Calendar, Clock, User, Building2, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Property {
  id: string;
  name: string;
  address: string;
}

interface Personnel {
  id: string;
  name: string;
  email: string | null;
}

interface CreateInspectionFormProps {
  onSuccess: (inspectionId: string) => void;
  onCancel: () => void;
}

export function CreateInspectionForm({ onSuccess, onCancel }: CreateInspectionFormProps) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    property_id: '',
    inspector_id: '',
    inspection_date: new Date().toISOString().split('T')[0],
    time_started: '',
  });

  useEffect(() => {
    loadProperties();
    loadPersonnel();
  }, []);

  const loadProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, address')
        .order('name');

      if (error) throw error;
      setProperties(data || []);
    } catch (err) {
      console.error('Error loading properties:', err);
    }
  };

  const loadPersonnel = async () => {
    try {
      const { data, error } = await supabase
        .from('maintenance_personnel')
        .select('id, name, email')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setPersonnel(data || []);
    } catch (err) {
      console.error('Error loading personnel:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.property_id) {
        throw new Error('Please select a property');
      }

      const { data, error } = await supabase
        .from('inspections')
        .insert({
          property_id: formData.property_id,
          inspector_id: formData.inspector_id || null,
          inspection_date: formData.inspection_date,
          time_started: formData.time_started || null,
          status: 'draft',
          completion_percentage: 0,
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        onSuccess(data.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create inspection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Schedule New Inspection</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Building2 size={18} />
            Property *
          </label>
          <select
            required
            value={formData.property_id}
            onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a property</option>
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name} - {property.address}
              </option>
            ))}
          </select>
          {properties.length === 0 && (
            <p className="mt-2 text-sm text-gray-500">
              No properties available. Please add a property first.
            </p>
          )}
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <User size={18} />
            Inspector
          </label>
          <select
            value={formData.inspector_id}
            onChange={(e) => setFormData({ ...formData, inspector_id: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select an inspector (optional)</option>
            {personnel.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name} {person.email ? `(${person.email})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar size={18} />
              Inspection Date *
            </label>
            <input
              type="date"
              required
              value={formData.inspection_date}
              onChange={(e) => setFormData({ ...formData, inspection_date: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Clock size={18} />
              Time Started
            </label>
            <input
              type="time"
              value={formData.time_started}
              onChange={(e) => setFormData({ ...formData, time_started: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={loading || properties.length === 0}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Start Inspection'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
